import Logger from '../lib/logger';

const NAME = 'TooltipCard';

// Behavior hooks, not styling classes: any markup with a `[data-tooltip]` wrapper around a
// `[data-tooltip-trigger]` button gets tap-to-toggle and Escape. Styles key off the state attributes.
const CARD = '[data-tooltip]';
const TRIGGER = '[data-tooltip-trigger]';
const OPEN = 'data-tooltip-open';
const DISMISSED = 'data-tooltip-dismissed';
// Optional: a panel centred under its trigger, nudged sideways to stay inside the viewport.
const PANEL = '[data-tooltip-panel]';
const NUDGE = '--pdp-tooltip-nudge';
const MAX_WIDTH = '--pdp-tooltip-max-width';
const VIEWPORT_GAP = 16;

/** Event targets are only EventTarget until narrowed, and text nodes have no closest(). */
const closest = (target: EventTarget | null, selector: string): Element | null =>
	target instanceof Element ? target.closest(selector) : null;

/**
 * Shifts the panel in from either edge of the visible area (the visual viewport, so pinch zoom
 * counts). Computed from its anchor and width rather than the panel's own box: its transform
 * transitions, so mid-animation it would report the old nudge.
 */
const keepPanelInViewport = (card: Element) => {
	const panel = card.querySelector<HTMLElement>(PANEL);
	// No offsetParent: inside a list hidden at this width, so there's nothing to place.
	const anchor = panel?.offsetParent;
	if (!panel || !anchor) return;

	// Pinch-zoomed, the visible area can be narrower than the panel's CSS cap; fit that first.
	const viewport = window.visualViewport;
	if (viewport) panel.style.setProperty(MAX_WIDTH, `${viewport.width - 2 * VIEWPORT_GAP}px`);

	const { left: anchorLeft, width: anchorWidth } = anchor.getBoundingClientRect();
	const left = anchorLeft + anchorWidth / 2 - panel.offsetWidth / 2;
	const right = left + panel.offsetWidth;
	const viewportLeft = (viewport?.offsetLeft ?? 0) + VIEWPORT_GAP;
	const viewportRight =
		(viewport ? viewport.offsetLeft + viewport.width : document.documentElement.clientWidth) - VIEWPORT_GAP;
	let nudge = 0;
	if (left < viewportLeft) nudge = viewportLeft - left;
	else if (right > viewportRight) nudge = viewportRight - right;
	panel.style.setProperty(NUDGE, `${nudge}px`);
};

/**
 * Hidden panels still take up layout, so one hanging past the edge widens the page (sideways
 * scroll on phones). Every panel is placed up front and again on resize, not only when opened.
 */
export const keepAllPanelsInViewport = (root: ParentNode = document) => {
	root.querySelectorAll(CARD).forEach(keepPanelInViewport);
};

/** Shown by hover, keyboard focus or a tap; Escape hides all of them, not only the focused one. */
const SHOWN = `${CARD}[${OPEN}], ${CARD}:hover, ${CARD}:focus-within`;

/** Dismissal lasts until both the pointer and focus have left, or :focus-visible would reopen it. */
const clearDismissedIfLeft = (card: Element, leaving: Node | null) => {
	if (card.contains(leaving)) return;
	if (card.matches(':hover') || card.contains(document.activeElement)) return;
	card.removeAttribute(DISMISSED);
};

/**
 * initTooltipCards - Keyboard and touch behavior for tooltips.
 */
export const initTooltipCards = () => {
	Logger.info(`${NAME} > initTooltipCards`);

	keepAllPanelsInViewport();
	// Late webfonts change label widths, and with them each icon's position.
	document.fonts.ready.then(() => keepAllPanelsInViewport());
	let resizeFrame = 0;
	const placeAllOnNextFrame = () => {
		cancelAnimationFrame(resizeFrame);
		resizeFrame = requestAnimationFrame(() => keepAllPanelsInViewport());
	};
	window.addEventListener('resize', placeAllOnNextFrame);
	window.visualViewport?.addEventListener('resize', placeAllOnNextFrame);
	// Panning while zoomed moves the visible area without resizing it.
	window.visualViewport?.addEventListener('scroll', placeAllOnNextFrame);

	document.addEventListener('click', (event) => {
		const trigger = closest(event.target, TRIGGER);
		const card = trigger?.closest(CARD);

		// A tapped-open tip has no hover to lose on touch screens; tapping anywhere else closes it.
		// Taps inside the open tip (selecting its text, following its link) leave it open.
		document.querySelectorAll(`${CARD}[${OPEN}]`).forEach((open) => {
			if (open !== card && !open.contains(event.target as Node | null)) open.removeAttribute(OPEN);
		});
		if (!card) return;
		keepPanelInViewport(card);
		card.removeAttribute(DISMISSED);
		card.toggleAttribute(OPEN);
	});

	// Before the panel shows: hover and keyboard focus open it from CSS, without a click.
	const placeOnEnter = (event: Event) => {
		const card = closest(event.target, CARD);
		if (card && !card.contains((event as MouseEvent | FocusEvent).relatedTarget as Node | null)) {
			keepPanelInViewport(card);
		}
	};
	document.addEventListener('mouseover', placeOnEnter);
	document.addEventListener('focusin', placeOnEnter);

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape') return;

		// WCAG 1.4.13 dismissable: hide without moving focus.
		document.querySelectorAll(SHOWN).forEach((card) => {
			card.removeAttribute(OPEN);
			card.setAttribute(DISMISSED, '');
		});
	});

	document.addEventListener('focusout', (event) => {
		const card = closest(event.target, CARD);
		if (card) clearDismissedIfLeft(card, event.relatedTarget as Node | null);
	});

	document.addEventListener('mouseout', (event) => {
		const card = closest(event.target, CARD);
		if (card) clearDismissedIfLeft(card, event.relatedTarget as Node | null);
	});
};

export default initTooltipCards;
